import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const LIVE_COUPON_SEEN_KEY = 'liveCouponSeenAt';

const Navbar = () => {
  const { user, logout, isAuthenticated, isPremium, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [premiumRequestCount, setPremiumRequestCount] = useState(0);
  const [liveCouponVisible, setLiveCouponVisible] = useState(false);
  const [liveCouponNotice, setLiveCouponNotice] = useState(null);
  const [liveCouponBadge, setLiveCouponBadge] = useState(false);
  const getNavClass = ({ isActive }) => (isActive ? 'nav-active' : '');
  const getPremiumPreviewPath = (feature) => `/premium-preview?feature=${feature}`;
  const getValuePath = () => '/premium-opportunities';
  const getPremiumPath = () => '/premium-coupons';

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/login');
  };

  const markLiveCouponSeen = (generatedAt) => {
    if (!generatedAt) return;
    localStorage.setItem(LIVE_COUPON_SEEN_KEY, generatedAt);
    setLiveCouponBadge(false);
    setLiveCouponNotice(null);
  };

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    let ignore = false;
    let intervalId;

    const loadLiveCouponStatus = async () => {
      if (!isAuthenticated) {
        setLiveCouponVisible(false);
        setLiveCouponBadge(false);
        setLiveCouponNotice(null);
        return;
      }

      try {
        const data = await api.getLiveCouponStatus();
        if (!ignore) {
          setLiveCouponVisible(Boolean(data.visible));
        }
      } catch (error) {
        if (!ignore) {
          setLiveCouponVisible(isAdmin);
        }
      }
    };

    const loadLiveCouponNotice = async () => {
      if (!isAuthenticated) return;

      try {
        const data = await api.getLiveCoupon();
        if (ignore) return;

        setLiveCouponVisible(Boolean(data.visible));

        const generatedAt = data?.coupon?.generatedAt || null;
        const seenAt = localStorage.getItem(LIVE_COUPON_SEEN_KEY);
        const isNewCoupon = Boolean(generatedAt && generatedAt !== seenAt);
        const canNotify = Boolean((isPremium || isAdmin) && data?.coupon && data?.visible);

        setLiveCouponBadge(Boolean(canNotify && isNewCoupon));
        setLiveCouponNotice(
          canNotify && isNewCoupon
            ? {
                generatedAt,
                title: 'Yeni canli kupon hazir',
                text: 'Canli Kupon menusunde yeni 2 maclik kupon olustu.'
              }
            : null
        );
      } catch (error) {
        if (!ignore) {
          setLiveCouponBadge(false);
          setLiveCouponNotice(null);
          loadLiveCouponStatus();
        }
      }
    };

    loadLiveCouponNotice();
    intervalId = window.setInterval(loadLiveCouponNotice, 60000);

    return () => {
      ignore = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [isAuthenticated, isAdmin, isPremium]);

  useEffect(() => {
    let ignore = false;
    let intervalId;

    const loadCount = async () => {
      if (!isAdmin) {
        setPremiumRequestCount(0);
        return;
      }

      try {
        const data = await api.getPremiumRequestCount();
        if (!ignore) {
          setPremiumRequestCount(Number(data.count) || 0);
        }
      } catch (error) {
        if (!ignore) {
          setPremiumRequestCount(0);
        }
      }
    };

    loadCount();
    if (isAdmin) {
      intervalId = window.setInterval(loadCount, 30000);
    }

    return () => {
      ignore = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [isAdmin]);

  return (
    <>
      <nav className="navbar">
        <Link to="/" className="brand-mark" onClick={closeMenu}>
          <img src="/tahminarena-logo.png" alt="TahminArena logo" className="brand-logo" />
          <div className="brand-copy">
            <strong>TahminArena</strong>
            <span>Futbol Tahmin Merkezi</span>
          </div>
        </Link>

        <div className="navbar-neon-slogan">Tahmin degil, veriyle hareket et.</div>

        <button
          type="button"
          className={`menu-toggle ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen((current) => !current)}
          aria-label="Menuyu ac"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {isAuthenticated ? (
          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <NavLink to="/" end className={getNavClass} onClick={closeMenu}>Ana Sayfa</NavLink>
            <NavLink to="/live" className={getNavClass} onClick={closeMenu}>Canli Maclar</NavLink>
            <NavLink to="/analysis" className={({ isActive }) => `${isActive ? 'nav-active ' : ''}premium-link`.trim()} onClick={closeMenu}>
              AI Analizi
            </NavLink>
            <NavLink to="/basketball" className={({ isActive }) => `${isActive ? 'nav-active ' : ''}premium-link`.trim()} onClick={closeMenu}>
              Basketbol
            </NavLink>
            <NavLink to="/basketball-live" className={({ isActive }) => `${isActive ? 'nav-active ' : ''}premium-link`.trim()} onClick={closeMenu}>
              Basketbol Canlı
            </NavLink>
            {(isPremium || isAdmin) ? (
              <NavLink to="/winning-premium-coupons" className={getNavClass} onClick={closeMenu}>Tutan Premium Kuponlar</NavLink>
            ) : (
              <Link to={getPremiumPreviewPath('winningPremiumCoupons')} className="premium-link" onClick={closeMenu}>Tutan Premium Kuponlar</Link>
            )}
            <NavLink to="/risky-analysis" className={({ isActive }) => `${isActive ? 'nav-active ' : ''}premium-link`.trim()} onClick={closeMenu}>
              Riskli Denenebilir
            </NavLink>
            <NavLink to="/premium-coupons" className={({ isActive }) => `${isActive ? 'nav-active ' : ''}premium-link`.trim()} onClick={closeMenu}>
              Premium Kuponlar
            </NavLink>
            <NavLink to="/premium-opportunities" className={({ isActive }) => `${isActive ? 'nav-active ' : ''}premium-link`.trim()} onClick={closeMenu}>
              VALUE BET
            </NavLink>
            {!isAdmin ? (
              <NavLink to="/account-deletion-request" className={getNavClass} onClick={closeMenu}>
                Hesap Silme
              </NavLink>
            ) : null}
            {isAdmin && <NavLink to="/admin" className={getNavClass} onClick={closeMenu}>Admin</NavLink>}
            {isAdmin && (
              <NavLink to="/admin/users" className={getNavClass} onClick={closeMenu}>
                Uyeler
                {premiumRequestCount > 0 ? <span className="nav-pill-alert">{premiumRequestCount}</span> : null}
              </NavLink>
            )}
            <div className="user-menu">
              <div className="user-badge">
                <span>{user?.name}</span>
                <small>{isAdmin ? 'ADMIN' : user?.role}</small>
              </div>
              <button onClick={handleLogout}>Cikis</button>
            </div>
          </div>
        ) : (
          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <Link to="/login" onClick={closeMenu}>Giris Yap</Link>
            <Link to="/register" className="btn-primary" onClick={closeMenu}>Kayit Ol</Link>
          </div>
        )}
      </nav>
      {isAuthenticated ? (
        <div className="mobile-bottom-nav">
          <NavLink to="/" end className={getNavClass}>
            <i className="fa-solid fa-house"></i>
            <span>Ana Sayfa</span>
          </NavLink>
          <NavLink to="/live" className={getNavClass}>
            <i className="fa-solid fa-tower-broadcast"></i>
            <span>Canli</span>
          </NavLink>
          <NavLink to="/analysis" className={getNavClass}>
            <i className="fa-solid fa-brain"></i>
            <span>AI Analiz</span>
          </NavLink>
          <NavLink to="/basketball" className={getNavClass}>
            <i className="fa-solid fa-basketball"></i>
            <span>Basket</span>
          </NavLink>
          <NavLink to="/basketball-live" className={getNavClass}>
            <i className="fa-solid fa-bolt"></i>
            <span>Canlı Basket</span>
          </NavLink>
          <NavLink to={getValuePath()} className={getNavClass}>
            <i className="fa-solid fa-chart-line"></i>
            <span>Value</span>
          </NavLink>
          <NavLink to={getPremiumPath()} className={getNavClass}>
            <i className="fa-solid fa-crown"></i>
            <span>Premium</span>
          </NavLink>
        </div>
      ) : null}
      {liveCouponNotice ? (
        <div className="live-coupon-notice">
          <div className="live-coupon-notice-copy">
            <strong>{liveCouponNotice.title}</strong>
            <span>{liveCouponNotice.text}</span>
          </div>
          <div className="live-coupon-notice-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                markLiveCouponSeen(liveCouponNotice.generatedAt);
                navigate('/live-coupon');
              }}
            >
              Ac
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => markLiveCouponSeen(liveCouponNotice.generatedAt)}
            >
              Kapat
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Navbar;
